import { Features } from '@/types'
import type { Plan } from '@/types'
import type { PlanFeatures } from '@/types/plans'
import { t } from '@/utils/helpers/translate'

// @TODO: refactor this with legacyPlans.filter((plan) => plan.self_service && !plan.legacy)
export const defaultLegacyPlans = ['community', 'entry', 'business'] as const

// these one need to be managed differently because of the different Stripe subscription flow they had
export const oldPlans = ['basic', 'premium', 'advanced', 'promo'] as const

// this should only contain the effective plan we have, level should be unique
// for any other special versions of plans, use PLANS_STATUS
export const legacyPlans = {
  starter: {
    name: 'starter',
    level: 0,
    self_service: true,
  },
  community: {
    name: 'community',
    level: 100,
    included_seats: 1,
    legacy: true,
    self_service: true,
  },
  entry: {
    name: 'entry',
    level: 200,
    legacy: true,
    self_service: true,
  },
  teams: {
    name: 'teams',
    level: 300,
    self_service: true,
    legacy: true,
  },
  business: {
    name: 'business',
    level: 301,
    legacy: true,
    self_service: true,
  },
  enterprise: {
    name: 'enterprise',
    level: 400,
    legacy: true,
    self_service: false,
  },
  enterprise_plus: {
    name: 'enterprise plus',
    level: 500,
    legacy: true,
    self_service: false,
  },
  enterprise_essentials: {
    name: 'enterprise essentials',
    level: 501,
    legacy: true,
    self_service: false,
  },
  enterprise_scale: {
    name: 'enterprise scale',
    level: 502,
    legacy: true,
    self_service: false,
  },
  enterprise_ultimate: {
    name: 'enterprise ultimate',
    level: 503,
    legacy: true,
    self_service: false,
  },
} as const

export const PLANS_STATUS = {
  trial: {
    name: 'trial',
    max_users: 10,
  },
  expired_trial: {
    max_users: 2,
  },
  development_mode: {
    name: 'learning mode',
    level: 301, // TODO - check if there is no conflict with new development mode plan
  },
  lowest_plan_without_cc: {
    max_users: 1,
  },
} as const

/**
 * The following info are used to display the plans details in upgrading/downgrading modals
 * @todo: these info should be fetched from a trusted external source and not be harcoded here
 */
const defaultFeatures: PlanFeatures[] = [
  {
    label: 'Access to SDKs',
    value: Features.SDKs,
  },
  {
    label: 'Access to blok.ink',
    value: Features.BlokInk,
  },
  {
    label: 'Activity Log',
    value: Features.Activity,
  },
  {
    label: 'Asset Manager',
    value: Features.Assets,
  },
  {
    label: 'Asset Folders',
    value: Features.AssetFolders,
  },
  {
    label: 'Autosave',
    value: Features.AutoSave,
  },
  {
    label: 'Clipboard History',
    value: Features.ClipboardHistory,
  },
  {
    label: 'Automatic Update',
    value: Features.AutomaticUpdate,
  },
  {
    label: 'Clone Projects',
    value: Features.CloneProjects,
  },
  {
    label: 'Content Delivery API',
    value: Features.DeliveryAPI,
  },
  {
    label: 'Custom Field Types',
    value: Features.CustomFieldTypes,
  },
  {
    label: 'Datasources',
    value: Features.Datasources,
  },
  {
    label: 'GraphQL',
    value: Features.GraphQL,
  },
  {
    label: 'Image Optimization Service',
    value: Features.ImageOptimization,
  },
  {
    label: 'Internationalization',
    value: Features.Internationalization,
  },
  {
    label: 'Management API',
    value: Features.ManagementAPI,
  },
  {
    label: 'Responsive Preview',
    value: Features.ResponsivePreview,
  },
  {
    label: 'Search Query',
    value: Features.SearchQuery,
  },
  {
    label: 'Standard Workflows',
    value: Features.Workflows,
  },
  {
    label: 'Documentation',
    value: Features.Docs,
  },
  {
    label: 'Languages',
    value: Features.Languages,
  },
  {
    label: 'Locales',
    value: Features.Locales,
  },
  {
    label: 'Webhooks',
    value: Features.WebHooks,
  },
  {
    label: 'Visual Editor',
    value: Features.VisualEditor,
  },
  {
    label: '2 Factor Authentication (2FA)',
    value: Features['2FA'],
  },
]

export const legacyPlansInfos: Readonly<Plan[]> = [
  {
    name: 'community',
    friendly_name: 'Community',
    users: legacyPlans.community.included_seats || 1,
    included_seats: legacyPlans.community.included_seats || 1,
    max_custom_roles: 0,
    max_users: 10,
    max_assets: 2500,
    max_stories: 25000,
    max_folders: 100,
    max_datasources: 10,
    max_webhooks: 3,
    max_preview_environments: 2,
    max_asset_custom_meta_data_count: 0,
    monthly_api_limit: 1000000,
    level: legacyPlans.community.level,
    index: 10, // index indicates the order of how the plan cards should be displayed, works together with is_visible
    is_visible: false,
    can_add_extra_users: true,
    priceIds: {
      month: {
        usd: {
          price: '0.00',
          id: 'price_1JaJ1GEORULcbN8Cv4sKyzZ5',
          staging_id: 'price_1JD2gQEORULcbN8C152eCRXu',
        },
        eur: {
          price: '0,00',
          id: 'price_1JaJ1GEORULcbN8Cw0VIijQR',
          staging_id: 'price_1JD2SjEORULcbN8Cm2JpOCw7',
        },
      },
      year: {
        usd: {
          price: '0.00',
          id: 'price_1JaJ1GEORULcbN8CxixxCZu8',
          staging_id: 'price_1JD2gEEORULcbN8CMQkiskYT',
        },
        eur: {
          price: '0,00',
          id: 'price_1JaJ1GEORULcbN8CnqkArdTZ',
          staging_id: 'price_1JD2VcEORULcbN8CKTKurlDx',
        },
      },
    },
    features: [...defaultFeatures],
    includedFeatures: [
      t('Up to 10 user seats'),
      t('Management API'),
      t('Visual Editor'),
      t('Datasources'),
    ],
  },
  {
    name: 'entry',
    friendly_name: 'Entry',
    users: 5,
    max_custom_roles: 4,
    max_users: 30,
    max_assets: 4000,
    max_stories: 40000,
    max_folders: 500,
    max_datasources: 15,
    max_webhooks: 3,
    max_preview_environments: 2,
    max_asset_custom_meta_data_count: 0,
    monthly_api_limit: 3000000,
    level: legacyPlans.entry.level,
    index: 20,
    is_visible: false,
    included_seats: 5, // this field seems to be used only for this plan
    can_add_extra_users: true,
    priceIds: {
      month: {
        usd: {
          price: '99.00',
          id: 'price_1JaJ18EORULcbN8CuNvdT9WW',
          staging_id: 'price_1JD2ZgEORULcbN8ClDhVud9R',
        },
        eur: {
          price: '99,00',
          id: 'price_1JaJ18EORULcbN8CGRhKpNM0',
          staging_id: 'price_1JD2V7EORULcbN8CoBxd4nAm',
        },
      },
      year: {
        usd: {
          price: '90.75',
          id: 'price_1JaJ18EORULcbN8CsoFyeUsG',
          staging_id: 'price_1JNGNhEORULcbN8CocuvopF4',
        },
        eur: {
          price: '90,75',
          id: 'price_1JaJ18EORULcbN8CV4iO53BB',
          staging_id: 'price_1JD2YVEORULcbN8CBdp0xT9v',
        },
      },
    },
    features: [
      ...defaultFeatures,
      // difference between community and entry features starts here
      {
        label: 'Approval App',
        value: Features.Approval,
      },
      {
        label: 'Custom Roles App',
        value: Features.CustomRoles,
      },
      {
        label: 'Content Locking App',
        value: Features.ContentLocking,
      },
      {
        label: 'Fine Graded Access Control',
        value: Features.AccessControl,
      },
      {
        label: 'Replace Assets App',
        value: Features.ReplaceAsset,
      },
      {
        label: 'SEO Meta Tags App',
        value: Features.SEOMetaTags,
      },
      {
        label: 'Task-Manager App',
        value: Features.TaskManager,
      },
      {
        label: 'Translatable Slugs App',
        value: Features.TranslatableSlugs,
      },
      {
        label: 'Webhook Secrets',
        value: Features.WebHookSecrets,
      },
    ],
    includedFeatures: [
      t('5 free included team member seats'),
      t('Up to 30 user seats'),
      t('500 GB traffic/month'),
      t('Custom roles'),
    ],
  },
  {
    name: 'teams',
    friendly_name: 'Teams',
    users: 10,
    max_custom_roles: 10,
    max_users: 40,
    max_assets: 10000,
    max_stories: 75000,
    max_folders: 1000,
    max_datasources: 20,
    max_webhooks: 3,
    max_preview_environments: 6,
    max_asset_custom_meta_data_count: 0,
    monthly_api_limit: 10000000,
    level: legacyPlans.teams.level,
    index: 21,
    is_visible: false,
    included_seats: 10,
    can_add_extra_users: true,
    legacy: true,
    priceIds: {
      month: {
        usd: {
          price: '449.00',
          id: 'price_1JaJ0tEORULcbN8CW3hkzvEq',
          staging_id: 'price_1JD2nTEORULcbN8CPawHRXJK',
        },
        eur: {
          price: '449,00',
          id: 'price_1JaJ0tEORULcbN8CG3Vu9ax7',
          staging_id: 'price_1JD2nTEORULcbN8CQ5cKVNgM',
        },
      },
      year: {
        usd: {
          price: '4,939.00',
          id: 'price_1JaJ0tEORULcbN8C23RlOb67',
          staging_id: 'price_1JD3dAEORULcbN8COjakU3bz',
        },
        eur: {
          price: '4.939,00',
          id: 'price_1JaJ0tEORULcbN8CFZ7zKscJ',
          staging_id: 'price_1JD2nTEORULcbN8CgNliJZOK',
        },
      },
    },
  },
  {
    name: 'business',
    friendly_name: 'Business',
    users: 20,
    max_custom_roles: 20,
    max_users: 40,
    max_assets: 10000,
    max_stories: 75000,
    max_folders: 1000,
    max_datasources: 20,
    max_webhooks: 5,
    max_preview_environments: 6,
    max_asset_custom_meta_data_count: 5,
    monthly_api_limit: 10000000,
    level: legacyPlans.business.level,
    index: 30,
    is_visible: false,
    included_seats: 20,
    can_add_extra_users: true,
    priceIds: {
      month: {
        usd: {
          price: '849.00',
          id: 'price_1NuCC1EORULcbN8Cbx62rIwF',
          staging_id: 'price_1NsmNtEORULcbN8CYQnmBLld',
        },
        eur: {
          price: '849,00',
          id: 'price_1NuCC1EORULcbN8CDRKOniVt',
          staging_id: 'price_1NsmLLEORULcbN8CTIGWBhcJ',
        },
      },
      year: {
        usd: {
          price: '778.25',
          id: 'price_1NuCC1EORULcbN8CNleN0TM6',
          staging_id: 'price_1NsmOHEORULcbN8C3IU9Cf9r',
        },
        eur: {
          price: '778,25',
          id: 'price_1NuCC1EORULcbN8C058pPlYI',
          staging_id: 'price_1NsmNZEORULcbN8CYOlmvJGb',
        },
      },
    },
    features: [
      ...defaultFeatures,
      // difference between community and entry features starts here
      {
        label: 'Approval App',
        value: Features.Approval,
      },
      {
        label: 'Custom Roles App',
        value: Features.CustomRoles,
      },
      {
        label: 'Content Locking App',
        value: Features.ContentLocking,
      },
      {
        label: 'Fine Graded Access Control',
        value: Features.AccessControl,
      },
      {
        label: 'Replace Assets App',
        value: Features.ReplaceAsset,
      },
      {
        label: 'SEO Meta Tags App',
        value: Features.SEOMetaTags,
      },
      {
        label: 'Task-Manager App',
        value: Features.TaskManager,
      },
      {
        label: 'Translatable Slugs App',
        value: Features.TranslatableSlugs,
      },
      {
        label: 'Webhook Secrets',
        value: Features.WebHookSecrets,
      },
      // difference between entry and teams features starts here
      {
        label: 'Access Token Scopes',
        value: Features.AccessTokenScope,
      },
      {
        label: 'Advanced Paths',
        value: Features.AdvancedPath,
      },
      {
        label: 'Custom Workflows',
        value: Features.CustomWorkflows,
      },
      {
        label: 'Collaboration/Comments',
        value: Features.Comments,
      },
      {
        label: 'Dimensions',
        value: Features.Dimensions,
      },
      {
        label: 'Forced 2 Factor Authentication (2FA)',
        value: Features.Forced2FA,
      },
      {
        label: 'Integration to Cloudinary Asset Manager',
        value: Features.Cloudinary,
      },
      {
        label: 'Integration to Optimizely',
        value: Features.Optimizely,
      },
      {
        label: 'Integration to Lokalise',
        value: Features.Lokalise,
      },
      {
        label: 'Integration to Shopify',
        value: Features.Shopify,
      },
      {
        label: 'Language Export/Import',
        value: Features.LanguageImportExport,
      },
      {
        label: 'Pipeline/Content Staging',
        value: Features.PipelineStaging,
      },
      {
        label: 'Scheduling & Releases',
        value: Features.Releases,
      },
      {
        label: 'Shared Components',
        value: Features.SharedComponents,
      },
      // difference between teams and business features starts here
      {
        label: 'Broken Link Checker App',
        value: Features.BrokenLinkChecker,
      },
      {
        label: 'Integration to Centra',
        value: Features.Centra,
      },
      {
        label: 'Integration to Saleor',
        value: Features.Saleor,
      },
      {
        label: 'S3 Backup Frequency',
        value: Features.S3BackupFrequency,
      },
    ],
    includedFeatures: [
      t('20 free included team member seats'),
      t('Up to 40 user seats'),
      t('1 TB traffic/month'),
      t('97% Uptime SLA'),
    ],
  },
  {
    name: 'enterprise',
    friendly_name: 'Enterprise',
    level: legacyPlans.enterprise.level,
    index: 10000,
    is_visible: true,
    users: 30,
    max_users: -1,
    legacy: true,
    included_seats: 30,
    includedFeatures: [
      t('Custom number of users included'),
      t('Custom limits for add. user seats'),
    ],
    can_add_extra_users: false,
  },
]
